import { useEffect, useState } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTypedParams } from 'react-router-typesafe-routes/dom';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { appRoute } from 'app/terminals/router/routes';
import { Loading } from 'app/ui';

import { TTerminalProfile } from '../../core/profiles';
import { getTerminalProfileRequest } from '../../requests/get-terminal-profile.request';
import { updateTerminalProfileRequest } from '../../requests/update-terminal-profile.request';
import { ProfileForm } from '../profile-form';

type Employee = Pick<TTerminalProfile['employees'][number], 'id' | 'name'>;

type NewTerminalProfile = Omit<TTerminalProfile, 'id' | 'employees'> & {
  employees: Employee[];
};

type UpdatedTerminalProfile = Omit<TTerminalProfile, 'employees'> & {
  employees: Employee[];
};

type FormFields = {
  name: string;
  employees: Employee[];
};

type ProfileTerminalId = Pick<TTerminalProfile, 'id'>;

export const DetailsProfile = observer(() => {
  const { id } = useTypedParams(appRoute.PROFILE);
  const notify = usePageNotify();
  const [fields] = useState(() =>
    observable.box<FormFields>({ name: '', employees: [] })
  );

  const getProfileResource = useResource<ProfileTerminalId, TTerminalProfile>({
    request: getTerminalProfileRequest,
    errorNotify: notify,
    onDone: (result) => {
      fields.set(result);
    },
  });

  const updateProfileResource = useResource<
    UpdatedTerminalProfile,
    TTerminalProfile
  >({
    request: updateTerminalProfileRequest,
    errorNotify: notify,
    onDone: (result) => {
      fields.set(result);

      notify.addMessage({
        type: 'success',
        message: 'Профиль успешно обновлен',
      });
    },
  });

  const handleSubmit = (fields: NewTerminalProfile) => {
    const updatedProfile: UpdatedTerminalProfile = {
      id: Number(id),
      ...fields,
    };

    updateProfileResource.send(updatedProfile);
  };

  const isLoading =
    getProfileResource.isLoading || updateProfileResource.isLoading;

  useEffect(() => {
    getProfileResource.send({ id: id || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Loading active={isLoading}>
      <ProfileForm onSubmit={handleSubmit} values={fields.get()} />
    </Loading>
  );
});
